import { PaginationState } from "@tanstack/react-table";
import { useState } from "react";
import {
  Button,
  TablePaginated,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { Icon } from "@sellar-io/components";
import { SellarImportsPayoutType } from "../types/common";
import { useSellarImportPayoutList } from "../hooks/useSellarImportPayoutList";
import { StatusBadge } from "./StatusBadge";
import { AdminOnly } from "../../../../../components/authFilter/AdminOnly";
import { CreateSellarPayoutDialog } from "./CreateSellarPayoutDialog";
import { SpinnerLegacy } from "../../../../../components";
import { formatDate } from "../../../../../utils/formatDate";

export const SellarImportsPayoutsTable = ({
  onSelectPayout,
}: {
  onSelectPayout: (id: number) => void;
}) => {
  const [createPayoutDialogActive, setCreatePayoutDialogActive] =
    useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const sellarImportPayoutsQuery = useSellarImportPayoutList({
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
  });
  const sellarImportsPayoutsList = sellarImportPayoutsQuery.data?.rows ?? [];

  const pageCount = Math.max(
    1,
    Math.ceil(
      (sellarImportPayoutsQuery.data?.count ?? 0) / pagination.pageSize,
    ),
  );

  const columnHelper = createSellarColumnHelper<SellarImportsPayoutType>();

  const columns = [
    columnHelper.accessor("id", {
      id: "id",
      header: "Payout #",
      enableSorting: false,
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row, {
      id: "capture-window",
      header: "Capture window",
      enableSorting: false,
      cell: (info) => {
        const { captureStart, captureEnd } = info.getValue();
        return (
          <span>
            {formatDate(captureStart, {
              format: "Do MMM YYYY",
            })}{" "}
            -{" "}
            {formatDate(captureEnd, {
              format: "Do MMM YYYY",
            })}
          </span>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      id: "status",
      header: "Status",
      enableSorting: false,
      cell: (info) => {
        const row = info.getValue();

        return <StatusBadge status={row.status} />;
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      enableSorting: false,
      cell: () => (
        <div className="flex w-full justify-end">
          <Icon icon="CHEVRON_RIGHT" size={1} />
        </div>
      ),
    }),
  ];

  if (sellarImportPayoutsQuery.isLoading) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      <AdminOnly>
        <div className="flex justify-end">
          <Button onClick={() => setCreatePayoutDialogActive(true)}>
            <Icon icon="PLUS" />
            Create payout
          </Button>
        </div>
      </AdminOnly>
      <div className="container mx-auto mt-6 max-w-[1300px]">
        <TablePaginated
          columns={columns}
          data={sellarImportsPayoutsList}
          pageCount={pageCount}
          pagination={pagination}
          setPagination={setPagination}
          onRowClick={(row) => {
            onSelectPayout(row.id);
          }}
          paginationLabel="Results per page"
          gridClasses="grid-cols-[repeat(3,_1fr)_40px]"
        />
      </div>

      <CreateSellarPayoutDialog
        active={createPayoutDialogActive}
        onDismiss={() => setCreatePayoutDialogActive(false)}
      />
    </>
  );
};
